import { DEFAULT_SIM_CONFIG, isEndlessHalf, type MatchState, type EntityId, type Vec3, type ActionKind, type TeamIdx, type CardKind, type PitchCondition } from '@fafi/shared';

// The renderer↔game contract (§13.5). render/ and ui/ consume ONLY ViewState + SimEvents, never
// MatchState/game/net internals (T-ARCH-004). Positions are in sim world coords (x,y horizontal, z up).

export interface ViewEntity {
  id: EntityId;
  team: TeamIdx;
  pos: Vec3;
  vel: { x: number; y: number }; // ground velocity (pass-target highlight needs the vector, D34)
  facing: number;
  action: ActionKind;
  actionTicks: number;
  moveSpeed: number;
  isControlled: boolean;
  isSwitchCandidate: boolean;
  isGk: boolean;
  card: CardKind | null;
  sentOff: boolean;
  /** D65c: seconds until a red-carded player returns from his 20 s suspension (0 = not suspended). Drives
   *  the HUD return-countdown clock so a sent-off player knows when he's back on. */
  suspendSecs: number;
  hasBall: boolean;
  shouting: boolean;
  calling: boolean; // D42: 'pass to me' signal — soft-lock favors callers
  speaking: boolean;
}
export interface ViewBall {
  pos: Vec3;
  /** D110: the live 3-D velocity — the landing ring re-flies the §5.3 model from it every frame. NOT
   *  interpolated: a lerped velocity between snapshots is not a velocity the sim ever had, and the ring
   *  must predict the same touchdown the server will produce. */
  vel: Vec3;
  /** Whoever is carrying it (null = loose) — a possessed ball has no flight to mark. */
  possessor: EntityId | null;
}
export interface ViewRef {
  pos: { x: number; y: number };
  viewDir: number;
}
export interface ViewHud {
  score: [number, number];
  clockDisplay: string;
  stamina: number;
  charge01: number;
  /** D43: the kick button being charged (BTN bit; 0 = not charging) + raw ticks — drives the trajectory preview. */
  chargeButton: number;
  chargeTicks: number;
  phase: string;
  /** D137 (PT4-P21): the controlled player is the KEEPER facing a penalty — the one context in which the
   *  client emits the dive bits at all. The sim is unchanged and still honours a pressed dive bit
   *  anywhere in the box, so §18 T-ACT-009 keeps its meaning and bot keepers keep their dive channel. */
  defendingPenalty: boolean;
  /** D135: a first-time finish is DECLARED (the sim is holding an armed SHOOT for me). The reticle
   *  distinguishes "press registered" from "contact possible", so a swallowed arm becomes visible. */
  aerialArmed: boolean;
}
export interface ViewState {
  entities: ViewEntity[];
  ball: ViewBall;
  ref: ViewRef;
  hud: ViewHud;
  /** Teammate a PASS would soft-lock right now (D34 receiver highlight), null when none/not possessing. */
  passTarget: EntityId | null;
  /** D65d: attackDir[0] — which way team 0 attacks. Flips at half-time; the team-colored goals recolor
   *  to the DEFENDER on the flip (so "your goal" is always your colour). */
  attackDir0: 1 | -1;
  /** D110: the live pitch condition — the landing ring re-flies the §5.3 model, which is condition-scaled
   *  (REST/KF_ROLL); marking a MUD cross with DRY physics would put the ring in the wrong place. */
  conditions: PitchCondition;
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const lerpAngle = (a: number, b: number, t: number): number => a + Math.atan2(Math.sin(b - a), Math.cos(b - a)) * t;

function formatClock(ticks: number): string {
  const secs = Math.floor(ticks / 30);
  const mm = String(Math.floor(secs / 60)).padStart(2, '0');
  const ss = String(secs % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

/** Build an interpolated ViewState between two sim ticks (alpha ∈ [0,1]). Live game + replay share this. */
export function toViewState(
  prev: MatchState,
  cur: MatchState,
  alpha: number,
  controlledId: EntityId,
  chargeCap = DEFAULT_SIM_CONFIG.actions.kick.CHARGE_CAP, // display normalization only; callers pass the wire cfg
): ViewState {
  const entities: ViewEntity[] = cur.players.map((p, i) => {
    const pp = prev.players[i] ?? p;
    return {
      id: p.id,
      team: p.team,
      pos: { x: lerp(pp.pos.x, p.pos.x, alpha), y: lerp(pp.pos.y, p.pos.y, alpha), z: lerp(pp.z, p.z, alpha) },
      vel: { x: lerp(pp.vel.x, p.vel.x, alpha), y: lerp(pp.vel.y, p.vel.y, alpha) },
      facing: lerpAngle(pp.facing, p.facing, alpha),
      action: p.action,
      actionTicks: p.actionTicks,
      moveSpeed: Math.hypot(p.vel.x, p.vel.y),
      isControlled: p.id === controlledId,
      isSwitchCandidate: false,
      isGk: p.isGk,
      card: p.card,
      sentOff: p.sentOff,
      suspendSecs: p.suspendedUntilTick > 0 ? Math.max(0, (p.suspendedUntilTick - cur.tick) / 30) : 0, // D65c return countdown
      hasBall: cur.ball.possessor === p.id,
      shouting: p.shouting,
      calling: p.calling,
      speaking: false,
    };
  });
  const ball: ViewBall = {
    pos: {
      x: lerp(prev.ball.pos.x, cur.ball.pos.x, alpha),
      y: lerp(prev.ball.pos.y, cur.ball.pos.y, alpha),
      z: lerp(prev.ball.pos.z, cur.ball.pos.z, alpha),
    },
    vel: { x: cur.ball.vel.x, y: cur.ball.vel.y, z: cur.ball.vel.z },
    possessor: cur.ball.possessor,
  };
  const ref: ViewRef = {
    pos: { x: lerp(prev.referee.pos.x, cur.referee.pos.x, alpha), y: lerp(prev.referee.pos.y, cur.referee.pos.y, alpha) },
    viewDir: cur.referee.viewDir,
  };
  const controlled = cur.players[controlledId];
  const hud: ViewHud = {
    score: [cur.score[0], cur.score[1]],
    clockDisplay: isEndlessHalf(cur.mode.halfLengthTicks) ? '' : formatClock(cur.clockTicks), // D144: practice shows no clock
    stamina: controlled?.stamina ?? 1,
    charge01: controlled?.charge ? Math.min(controlled.charge.ticks, chargeCap) / chargeCap : 0,
    chargeButton: controlled?.charge?.button ?? 0,
    chargeTicks: controlled?.charge?.ticks ?? 0,
    phase: cur.phase,
    defendingPenalty: cur.restart !== null && cur.restart.kind === 'PENALTY' && controlled !== undefined && controlled.isGk && controlled.team !== cur.restart.team,
    aerialArmed: ((controlled?.intentBits ?? 0) & 8) !== 0, // BTN.SHOOT
  };
  return { entities, ball, ref, hud, passTarget: null, attackDir0: cur.attackDir[0], conditions: cur.conditions };
}
